import { useCallback, useEffect, useState } from 'react'
import { UserMinus, RefreshCw, Plus, AlertTriangle, Clock, X } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

interface Leaver {
  id: number
  employee_id: string
  name: string
  email: string
  department: string
  role: string
  manager_email: string | null
  leave_date: string
  status: string
  source: string
  notes: string
  discovered_assets: string[]
  discovered_accounts: string[]
}

interface Stats {
  by_status: Record<string, number>
  urgency: { this_week: number; this_month: number; later: number }
  by_dept: { dept: string; n: number }[]
}

const URGENCY_TONE = (days: number) =>
  days < 0   ? 'text-text-muted'
  : days < 3 ? 'text-rose font-bold'
  : days < 8 ? 'text-orange'
  : days < 31? 'text-amber-light'
  : 'text-text-secondary'

export default function LeaversPage() {
  const [leavers, setLeavers] = useState<Leaver[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({
    employee_id: '', name: '', email: '', department: '', role: '',
    manager_email: '', leave_date: '', notes: '',
  })

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [up, st] = await Promise.allSettled([
        api.get('/api/leavers', { params: { days_ahead: 90, days_back: 30, limit: 200 } }),
        api.get('/api/leavers/stats'),
      ])
      if (up.status === 'fulfilled')  setLeavers(up.value.data.leavers || [])
      if (st.status === 'fulfilled')  setStats(st.value.data)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  const create = async () => {
    if (!form.email || !form.name || !form.leave_date) return
    try {
      await api.post('/api/leavers', { ...form, leave_date: new Date(form.leave_date).toISOString() })
      setShowForm(false)
      setForm({ employee_id: '', name: '', email: '', department: '', role: '', manager_email: '', leave_date: '', notes: '' })
      fetchAll()
    } catch {}
  }

  const remove = async (id: number) => {
    if (!confirm('Remove this leaver record?')) return
    await api.delete(`/api/leavers/${id}`)
    fetchAll()
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <UserMinus size={24} className="text-amber" /> Leavers
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Upcoming offboarding — cross-references discovered assets + accounts (when HRIS connector lands)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchAll} className="btn-ghost text-xs">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary text-xs">
            <Plus size={12} /> Add leaver
          </button>
        </div>
      </div>

      {/* Urgency tiles */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile color="rose"     label="This week"  value={stats.urgency?.this_week ?? 0} icon={AlertTriangle} />
          <Tile color="amber"    label="This month" value={stats.urgency?.this_month ?? 0} icon={Clock} />
          <Tile color="emerald"  label="Later"      value={stats.urgency?.later ?? 0}     icon={Clock} />
          <Tile color="blue"     label="Total open" value={Object.values(stats.by_status || {}).reduce((s, n) => s + n, 0)} icon={UserMinus} />
        </div>
      )}

      {/* Form (modal-lite) */}
      {showForm && (
        <div className="card p-4 border border-blue/40">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">Add leaver</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-text-muted hover:text-text-primary">
              <X size={14} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <label className="space-y-1"><span className="text-text-muted">Employee ID</span>
              <input value={form.employee_id} onChange={e => setForm({...form, employee_id: e.target.value})} className="input w-full py-1.5" placeholder="EMP-1234" /></label>
            <label className="space-y-1"><span className="text-text-muted">Name</span>
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="input w-full py-1.5" placeholder="Jane Smith" /></label>
            <label className="space-y-1"><span className="text-text-muted">Email</span>
              <input value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="input w-full py-1.5" placeholder="jane.smith@…" /></label>
            <label className="space-y-1"><span className="text-text-muted">Department</span>
              <input value={form.department} onChange={e => setForm({...form, department: e.target.value})} className="input w-full py-1.5" /></label>
            <label className="space-y-1"><span className="text-text-muted">Role</span>
              <input value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="input w-full py-1.5" /></label>
            <label className="space-y-1"><span className="text-text-muted">Manager email</span>
              <input value={form.manager_email} onChange={e => setForm({...form, manager_email: e.target.value})} className="input w-full py-1.5" /></label>
            <label className="space-y-1"><span className="text-text-muted">Leave date</span>
              <input type="datetime-local" value={form.leave_date} onChange={e => setForm({...form, leave_date: e.target.value})} className="input w-full py-1.5" /></label>
            <label className="space-y-1 md:col-span-2"><span className="text-text-muted">Notes</span>
              <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} className="input w-full py-1.5" /></label>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-xs">Cancel</button>
            <button type="button" onClick={create} className="btn-primary text-xs">Create</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card p-3">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                <th className="text-left py-2 px-2">Name</th>
                <th className="text-left py-2 px-2">Department</th>
                <th className="text-left py-2 px-2">Leave date</th>
                <th className="text-left py-2 px-2">Status</th>
                <th className="text-left py-2 px-2">Source</th>
                <th className="text-right py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {leavers.length === 0 && (
                <tr><td colSpan={6} className="text-center py-6 text-text-muted text-[11px] italic">
                  No leaver records. Add one manually or wire an HRIS connector
                  (Workday / BambooHR / Personio / SuccessFactors).
                </td></tr>
              )}
              {leavers.map(l => {
                const days = (new Date(l.leave_date).getTime() - Date.now()) / (24 * 3600 * 1000)
                return (
                  <tr key={l.id} className="border-b border-border/50">
                    <td className="py-1.5 px-2">
                      <div className="text-text-primary font-medium">{l.name}</div>
                      <div className="text-[10px] text-text-muted">{l.email}</div>
                    </td>
                    <td className="py-1.5 px-2">
                      <div className="text-text-secondary">{l.department || '—'}</div>
                      <div className="text-[10px] text-text-muted">{l.role || ''}</div>
                    </td>
                    <td className={clsx('py-1.5 px-2 font-mono', URGENCY_TONE(days))}>
                      {new Date(l.leave_date).toLocaleDateString()}
                      <div className="text-[10px] font-sans">
                        {days >= 0 ? `in ${Math.floor(days)}d` : `${Math.abs(Math.floor(days))}d ago`}
                      </div>
                    </td>
                    <td className="py-1.5 px-2">
                      <span className={clsx('badge text-[9px]',
                        l.status === 'upcoming' ? 'bg-amber/15 text-amber-light border border-amber/40'
                        : l.status === 'completed' ? 'bg-emerald/15 text-emerald border border-emerald/40'
                        : 'bg-surface-raised text-text-muted border border-border')}>
                        {l.status}
                      </span>
                    </td>
                    <td className="py-1.5 px-2 text-text-muted">{l.source}</td>
                    <td className="py-1.5 px-2 text-right">
                      <button type="button" onClick={() => remove(l.id)} className="text-text-muted hover:text-rose">
                        <X size={11} />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[10px] text-text-muted text-center">
        HRIS connector framework: when a Workday/BambooHR/Personio connector is enabled,
        upcoming leavers will populate automatically and cross-reference discovered assets.
      </div>
    </div>
  )
}

function Tile({ color, label, value, icon: Icon }: { color: string; label: string; value: number; icon: any }) {
  const tone =
    color === 'rose'    ? 'text-rose'        :
    color === 'amber'   ? 'text-amber-light' :
    color === 'emerald' ? 'text-emerald'     :
                          'text-blue-light'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted flex items-center gap-1">
        <Icon size={10} className={tone} /> {label}
      </div>
      <div className={clsx('text-2xl font-bold mt-1 font-mono', tone)}>{value}</div>
    </div>
  )
}
